import React, { useState, useEffect, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FilterPanel from "./../common/FilterPanel";
import FilterChips, { countActive } from "./../common/FilterChips";
import { BROADCAST_FILTERS } from "./../../config/filterConfigs";
import {
  faBell, faChartBar, faChartLine, faCircle, faClock,
  faCheckCircle, faBan,
  faExclamationTriangle, faArrowRight, faRotate,
  faMobileAlt, faShieldAlt, faFire, faBullhorn, faRobot, faStar,
  faChevronLeft, faChevronRight, faXmark, faFilter,
} from "@fortawesome/free-solid-svg-icons";

// ─── Formatting helpers ───────────────────────────────────────────────────────
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";
const fmtNum = (n) => (n != null ? Number(n).toLocaleString() : "–");
const fmtPct = (n) => (n != null ? Number(n).toFixed(1) + "%" : "–");

// ─── Segment colours ─────────────────────────────────────────────────────────
const SEGMENT_COLORS = {
  ALL: "#6366F1",
  FREE: "#64748B",
  FREE_OLD: "#DC2626",
  SPARK_BUNDLE: "#6366F1",
  PREMIUM: "#7C3AED",
  ELITE: "#F59E0B",
  ANNUAL: "#059669",
  PAID: "#2563EB",
  INACTIVE_30D: "#EA580C",
  NO_ALIGNMENT: "#DB2777",
  EXPIRING_7D: "#D97706",
};

const SEGMENT_LABELS = {
  ALL: "All Users",
  FREE: "Free",
  FREE_OLD: "Free 7d+",
  SPARK_BUNDLE: "Spark Bundle",
  PREMIUM: "Premium",
  ELITE: "Elite",
  ANNUAL: "Annual",
  PAID: "All Paid",
  INACTIVE_30D: "Inactive 30d",
  NO_ALIGNMENT: "No Alignment",
  EXPIRING_7D: "Expiring 7d",
};

const STATUS_CONFIG = {
  sent: { color: "#059669", bg: "#D1FAE5", label: "Sent" },
  scheduled: { color: "#D97706", bg: "#FEF3C7", label: "Scheduled" },
  failed: { color: "#DC2626", bg: "#FEE2E2", label: "Failed" },
  cancelled: { color: "#6B7280", bg: "#F3F4F6", label: "Cancelled" },
};

const CRON_CATEGORIES = ["All", "re-engagement", "upsell", "behavioral", "trigger", "transactional"];

// ─── Skeleton placeholder ─────────────────────────────────────────────────────
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

// ─── Small reusable Badge ─────────────────────────────────────────────────────
const Badge = ({ label, color, bg }) => (
  <span
    className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
    style={{ color, backgroundColor: bg }}
  >
    {label}
  </span>
);

// ─── KPI Card ────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, iconColor, title, value, sub, loading }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-1 relative overflow-hidden">
    <div
      className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center opacity-15"
      style={{ backgroundColor: iconColor }}
    />
    <FontAwesomeIcon
      icon={icon}
      className="absolute top-5 right-5 text-base"
      style={{ color: iconColor }}
    />
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
    {loading ? (
      <Skeleton className="h-8 w-32 mt-1" />
    ) : (
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    )}
    <p className="text-xs text-gray-400">{sub}</p>
  </div>
);

// ─── Stacked Bar Chart (SVG) ─────────────────────────────────────────────────
const SendsByDayChart = ({ data }) => {
  const [hovered, setHovered] = useState(null);
  if (!data || data.length === 0) return <Skeleton className="h-32 w-full" />;

  const W = 700, H = 120, PAD_L = 10, PAD_B = 18;
  const chartH = H - PAD_B;
  const maxVal = Math.max(...data.map((d) => d.total), 1);
  const barW = Math.max(Math.floor((W - PAD_L * 2) / data.length) - 1, 2);

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="auto"
        style={{ display: "block" }}
      >
        {data.map((d, i) => {
          const bH = Math.max((d.broadcasts / maxVal) * (chartH - 4), d.broadcasts > 0 ? 2 : 0);
          const cH = Math.max((d.crons / maxVal) * (chartH - 4), d.crons > 0 ? 2 : 0);
          const x = PAD_L + i * (barW + 1);
          const showLabel = i % 5 === 0;
          const labelDate = new Date(d.date);
          const labelStr = labelDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

          return (
            <g
              key={d.date}
              onMouseEnter={() => setHovered({ ...d, x, i })}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: "pointer" }}
            >
              {/* Broadcast bar (bottom, indigo) */}
              <rect
                x={x}
                y={chartH - bH}
                width={barW}
                height={bH}
                fill={hovered?.date === d.date ? "#4F46E5" : "#6366F1"}
                rx="1"
              />
              {/* Cron bar (top, violet) */}
              <rect
                x={x}
                y={chartH - bH - cH}
                width={barW}
                height={cH}
                fill={hovered?.date === d.date ? "#6D28D9" : "#7C3AED"}
                rx="1"
              />
              {showLabel && (
                <text
                  x={x + barW / 2}
                  y={H - 2}
                  textAnchor="middle"
                  fontSize="7"
                  fill="#9CA3AF"
                >
                  {labelStr}
                </text>
              )}
            </g>
          );
        })}
        {/* Y-axis line */}
        <line x1={PAD_L} y1={0} x2={PAD_L} y2={chartH} stroke="#E5E7EB" strokeWidth="1" />
        <line x1={PAD_L} y1={chartH} x2={W - PAD_L} y2={chartH} stroke="#E5E7EB" strokeWidth="1" />
      </svg>

      {/* Tooltip */}
      {hovered && (
        <div
          className="absolute pointer-events-none z-10 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl"
          style={{
            left: Math.min(hovered.x + 10, 560),
            top: 8,
            minWidth: 150,
          }}
        >
          <p className="font-semibold mb-1">
            {new Date(hovered.date).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </p>
          <p>
            <span className="inline-block w-2 h-2 rounded-full bg-indigo-400 mr-1" />
            Broadcasts: {fmtNum(hovered.broadcasts)}
          </p>
          <p>
            <span className="inline-block w-2 h-2 rounded-full bg-violet-400 mr-1" />
            Crons: {fmtNum(hovered.crons)}
          </p>
          <p className="border-t border-gray-600 mt-1 pt-1 font-semibold">
            Total: {fmtNum(hovered.total)}
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-indigo-500 inline-block" />
          Broadcasts
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-violet-600 inline-block" />
          Behavioral Crons
        </span>
      </div>
    </div>
  );
};

// ─── Channel Breakdown horizontal bars ───────────────────────────────────────
// Labels come from the API (CRON_META.label) — no client-side map needed.
const ChannelBreakdown = ({ data }) => {
  if (!data || data.length === 0) return <Skeleton className="h-40 w-full" />;
  const sorted = [...data].sort((a, b) => b.total - a.total);
  const maxTotal = Math.max(...sorted.map((c) => c.total), 1);

  return (
    <div className="space-y-2.5">
      {sorted.map((item) => (
        <div key={item.channel} className="flex items-center gap-3">
          <span className="text-xs text-gray-600 w-44 truncate flex-shrink-0">
            {item.label || item.channel}
          </span>
          <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full"
              style={{
                width: `${(item.total / maxTotal) * 100}%`,
                background: "linear-gradient(to right, #6366F1, #7C3AED)",
              }}
            />
          </div>
          <span className="text-xs font-semibold text-gray-700 w-14 text-right">
            {fmtNum(item.total)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Donut SVG ────────────────────────────────────────────────────────────────
const DonutChart = ({ pct, label }) => {
  const R = 60, C = 80;
  const circumference = 2 * Math.PI * R;
  const stroke = ((pct || 0) / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx={C} cy={C} r={R} fill="none" stroke="#E5E7EB" strokeWidth="14" />
        <circle
          cx={C}
          cy={C}
          r={R}
          fill="none"
          stroke="#6366F1"
          strokeWidth="14"
          strokeDasharray={`${stroke} ${circumference}`}
          transform={`rotate(-90 ${C} ${C})`}
          strokeLinecap="round"
        />
        <text
          x={C}
          y={C - 8}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="20"
          fontWeight="700"
          fill="#1F2937"
        >
          {fmtPct(pct)}
        </text>
        <text
          x={C}
          y={C + 16}
          textAnchor="middle"
          fontSize="9"
          fill="#9CA3AF"
        >
          {label}
        </text>
      </svg>
    </div>
  );
};

// ─── Sparkline for cron cards ─────────────────────────────────────────────────
const Sparkline = ({ data, color }) => {
  const max = Math.max(...(data || []), 1);
  const allZero = (data || []).every((v) => v === 0);

  if (allZero) {
    return <p className="text-xs text-gray-400 italic mt-1">No runs in 7 days</p>;
  }

  return (
    <div className="flex items-end gap-0.5 h-8 mt-1">
      {(data || []).map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm transition-all"
          style={{
            height: `${Math.max((v / max) * 100, v > 0 ? 8 : 3)}%`,
            backgroundColor: v > 0 ? color : "#E5E7EB",
            minHeight: 2,
          }}
        />
      ))}
    </div>
  );
};

// ─── Cron status badge ────────────────────────────────────────────────────────
const CronStatus = ({ lastRunAt }) => {
  if (!lastRunAt)
    return <Badge label="Never run" color="#6B7280" bg="#F3F4F6" />;
  const ago = Date.now() - new Date(lastRunAt).getTime();
  if (ago > 24 * 60 * 60 * 1000)
    return <Badge label="Stale" color="#D97706" bg="#FEF3C7" />;
  return <Badge label="Active" color="#059669" bg="#D1FAE5" />;
};

// ─── Inline progress bar ─────────────────────────────────────────────────────
const InlineBar = ({ pct, color = "#7C3AED" }) => (
  <div className="w-full bg-gray-100 rounded-full h-1.5 mt-0.5">
    <div
      className="h-1.5 rounded-full"
      style={{ width: `${Math.min(pct || 0, 100)}%`, backgroundColor: color }}
    />
  </div>
);

// ─── Slide-over panel ────────────────────────────────────────────────────────
const SlideOver = ({ broadcast: b, onClose }) => {
  if (!b) return null;
  const segColor = SEGMENT_COLORS[b.audience?.segment] || "#6366F1";
  const sCfg = STATUS_CONFIG[b.status] || STATUS_CONFIG.sent;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-40"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="bg-[linear-gradient(to_right,_#19224B,_#4C1D95)] p-5 flex items-start justify-between">
          <div className="flex-1 pr-4">
            <p className="text-white font-bold text-lg leading-snug">{b.title}</p>
            <p className="text-indigo-200 text-sm mt-1">{b.body || "–"}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white opacity-70 hover:opacity-100 mt-0.5"
          >
            <FontAwesomeIcon icon={faXmark} className="text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Estimated Reach", val: fmtNum(b.audience?.estimatedReach) },
              { label: "Sent", val: fmtNum(b.sentCount) },
              { label: "Failed", val: fmtNum(b.failedCount) },
              { label: "Delivery Rate", val: fmtPct(b.deliveryRate) },
              { label: "Opened", val: fmtNum(b.openedCount) },
              { label: "Open Rate", val: fmtPct(b.openRate) },
            ].map(({ label, val }) => (
              <div key={label} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-lg font-bold text-gray-900 mt-0.5">{val}</p>
              </div>
            ))}
          </div>

          {/* Meta */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 w-24">Segment</span>
              <Badge
                label={SEGMENT_LABELS[b.audience?.segment] || b.audience?.segment || "–"}
                color="#fff"
                bg={segColor}
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 w-24">Status</span>
              <Badge label={sCfg.label} color={sCfg.color} bg={sCfg.bg} />
            </div>
            {b.deepLink && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500 w-24">Deep Link</span>
                <span className="text-indigo-600 font-mono text-xs">{b.deepLink}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 w-24">Sent At</span>
              <span className="text-gray-700">{fmtDate(b.sentAt)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 w-24">Created At</span>
              <span className="text-gray-700">{fmtDate(b.createdAt)}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Tab: Overview ────────────────────────────────────────────────────────────
const OverviewTab = ({ period, tokenHealth }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await func.get(`mst-admin/notifications/analytics/overview?days=${period}`);
      setData(res?.data?.data || res?.data || null);
    } catch {
      setError("Could not load overview data.");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = [
    {
      icon: faBullhorn,
      iconColor: "#6366F1",
      title: "Total Sent",
      value: loading ? "–" : fmtNum(data?.totalSent),
      sub: `Last ${period}d`,
    },
    {
      icon: faCheckCircle,
      iconColor: "#059669",
      title: "Delivery Rate",
      value: loading ? "–" : fmtPct(data?.deliveryRate),
      sub: "Of FCM attempts",
    },
    {
      icon: faChartLine,
      iconColor: "#7C3AED",
      title: "Open Rate",
      value: loading ? "–" : fmtPct(data?.openRate),
      sub: "Of delivered",
    },
    {
      icon: faMobileAlt,
      iconColor: "#2563EB",
      title: "Active Tokens",
      value: loading ? "–" : fmtNum(data?.activeTokens),
      sub: "Registered devices",
    },
  ];

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl mb-3" />
        <p className="text-sm mb-3">{error}</p>
        <button
          onClick={load}
          className="text-indigo-600 text-sm font-medium flex items-center gap-1 hover:underline"
        >
          <FontAwesomeIcon icon={faRotate} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <KpiCard key={k.title} {...k} loading={loading} />
        ))}
      </div>

      {/* Sends by Day */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faChartBar} className="text-indigo-500" />
            Sends by Day
          </h3>
          <span className="text-xs text-gray-400">Stacked: Broadcasts + Crons</span>
        </div>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <SendsByDayChart data={data?.sendsByDay || []} />
        )}
      </div>

      {/* Channel breakdown + System Health */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        {/* Channel breakdown */}
        <div className="xl:col-span-3 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon={faCircle} className="text-violet-500 text-xs" />
            Channel Breakdown
          </h3>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <Skeleton key={n} className="h-4 w-full" />
              ))}
            </div>
          ) : (
            <ChannelBreakdown data={data?.channelBreakdown || []} />
          )}
        </div>

        {/* System health */}
        <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faShieldAlt} className="text-green-500" />
            System Health
          </h3>

          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-50">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} className="text-yellow-500" />
                Scheduled Pending
              </span>
              {loading ? (
                <Skeleton className="h-5 w-8" />
              ) : (
                <Badge
                  label={data?.scheduledPending ?? "0"}
                  color="#D97706"
                  bg="#FEF3C7"
                />
              )}
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-50">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <FontAwesomeIcon icon={faFire} className="text-green-500" />
                Active Triggers
              </span>
              {loading ? (
                <Skeleton className="h-5 w-8" />
              ) : (
                <Badge
                  label={data?.activeTriggersCount ?? "0"}
                  color="#059669"
                  bg="#D1FAE5"
                />
              )}
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <FontAwesomeIcon icon={faBan} className="text-red-400" />
                Capped Today
              </span>
              {!tokenHealth ? (
                <Skeleton className="h-5 w-10" />
              ) : (
                <Badge
                  label={fmtNum(tokenHealth.capHitsToday)}
                  color="#DC2626"
                  bg="#FEE2E2"
                />
              )}
            </div>
          </div>

          {/* Quick links */}
          <div className="mt-auto pt-3 border-t border-gray-100 space-y-2">
            <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-2">
              Quick Links
            </p>
            <button
              onClick={() => navigate("/notifications/broadcast")}
              className="w-full flex items-center justify-between text-sm bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium px-3 py-2 rounded-lg transition-colors"
            >
              <span>
                <FontAwesomeIcon icon={faBullhorn} className="mr-2" />
                New Broadcast
              </span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
            <button
              onClick={() => navigate("/notification-triggers")}
              className="w-full flex items-center justify-between text-sm bg-violet-50 hover:bg-violet-100 text-violet-700 font-medium px-3 py-2 rounded-lg transition-colors"
            >
              <span>
                <FontAwesomeIcon icon={faFire} className="mr-2" />
                Manage Triggers
              </span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Broadcasts ──────────────────────────────────────────────────────────
const BroadcastsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const LIMIT = 20;
  const [filters, setFilters] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const load = useCallback(async (p = 1, f = {}) => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ page: p, limit: LIMIT });
      Object.entries(f).forEach(([k, v]) => { if (v) qs.append(k, v); });
      const res = await func.get(`mst-admin/notifications/analytics/broadcasts?${qs}`);
      setData(res?.data?.data || res?.data || null);
    } catch {
      setError("Could not load broadcasts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, page, reload]);

  const handleCancel = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Cancel this scheduled broadcast?")) return;
    setCancelling(id);
    try {
      await func.del(`mst-admin/notifications/broadcast/${id}`);
      load(page, filters);
    } catch {
      alert("Failed to cancel broadcast.");
    } finally {
      setCancelling(null);
    }
  };

  const broadcasts = data?.broadcasts || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / LIMIT);

  // Summary stats
  const avgOpenRate =
    broadcasts.length > 0
      ? broadcasts.reduce((s, b) => s + Number(b.openRate || 0), 0) / broadcasts.length
      : 0;
  const best = broadcasts.reduce(
    (m, b) => (Number(b.openRate || 0) > Number(m?.openRate || 0) ? b : m),
    null
  );

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl mb-3" />
        <p className="text-sm mb-3">{error}</p>
        <button
          onClick={() => load(page)}
          className="text-indigo-600 text-sm font-medium flex items-center gap-1 hover:underline"
        >
          <FontAwesomeIcon icon={faRotate} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Broadcasts", val: loading ? null : fmtNum(total), icon: faBullhorn, color: "#6366F1" },
          { label: "Avg Open Rate", val: loading ? null : fmtPct(avgOpenRate), icon: faChartLine, color: "#7C3AED" },
          { label: "Best Performer", val: loading ? null : (best ? fmtPct(best.openRate) : "–"), icon: faStar, color: "#F59E0B" },
        ].map(({ label, val, icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: color + "18" }}
            >
              <FontAwesomeIcon icon={icon} style={{ color }} />
            </div>
            <div>
              <p className="text-xs text-gray-500">{label}</p>
              {val == null ? (
                <Skeleton className="h-6 w-16 mt-0.5" />
              ) : (
                <p className="text-xl font-bold text-gray-900">{val}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <button onClick={() => setShowFilter(true)} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition">
          Filters
          {countActive(BROADCAST_FILTERS, filters) > 0 && (<span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-indigo-600 text-white rounded-full w-5 h-5">{countActive(BROADCAST_FILTERS, filters)}</span>)}
        </button>
        <FilterChips config={BROADCAST_FILTERS} filters={filters} onClearKeys={(keys) => { const n = { ...filters }; keys.forEach((k) => { n[k] = ""; }); setFilters(n); setPage(1); setReload((r) => !r); }} onClearAll={() => { setFilters({}); setPage(1); setReload((r) => !r); }} />
      </div>
      <FilterPanel isOpen={showFilter} onClose={() => setShowFilter(false)} title="Filter Broadcasts" config={BROADCAST_FILTERS} filters={filters} onChange={setFilters} onApply={() => { setPage(1); setReload((r) => !r); }} onReset={() => { setFilters({}); setPage(1); setReload((r) => !r); }} />

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {["Message", "Segment", "Reach", "Delivery", "Opened", "Status", "When", ""].map(
                  (h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 8 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                : broadcasts.map((b) => {
                    const segColor = SEGMENT_COLORS[b.audience?.segment] || "#6366F1";
                    const sCfg = STATUS_CONFIG[b.status] || STATUS_CONFIG.sent;
                    return (
                      <tr
                        key={b._id}
                        className="hover:bg-indigo-50/30 cursor-pointer transition-colors"
                        onClick={() => setSelected(b)}
                      >
                        <td className="px-4 py-3 max-w-xs">
                          <p className="font-semibold text-gray-900 truncate">{b.title}</p>
                          <p className="text-xs text-gray-400 truncate">{b.body}</p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                            style={{ backgroundColor: segColor }}
                          >
                            {SEGMENT_LABELS[b.audience?.segment] || b.audience?.segment || "–"}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          <span className="font-medium">{fmtNum(b.sentCount)}</span>
                          <span className="text-gray-400">
                            /{fmtNum(b.audience?.estimatedReach)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {b.deliveryRate != null ? (
                            <Badge
                              label={fmtPct(b.deliveryRate)}
                              color="#059669"
                              bg="#D1FAE5"
                            />
                          ) : (
                            <span className="text-gray-300">–</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" style={{ minWidth: 90 }}>
                          <p className="text-sm font-semibold text-violet-700">
                            {fmtPct(b.openRate)}
                          </p>
                          <InlineBar pct={Number(b.openRate || 0)} color="#7C3AED" />
                          <p className="text-xs text-gray-400 mt-0.5">
                            {fmtNum(b.openedCount)} opens
                          </p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge label={sCfg.label} color={sCfg.color} bg={sCfg.bg} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                          {fmtDate(b.sentAt || b.scheduledAt)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {b.status === "scheduled" && (
                            <button
                              onClick={(e) => handleCancel(b._id, e)}
                              disabled={cancelling === b._id}
                              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 font-medium"
                            >
                              <FontAwesomeIcon icon={faBan} />
                              {cancelling === b._id ? "..." : "Cancel"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {page} of {totalPages} — {fmtNum(total)} total
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
              >
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Slide-over */}
      <SlideOver broadcast={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

// ─── Tab: Behavioral Crons ────────────────────────────────────────────────────
const CronsTab = () => {
  const [crons, setCrons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await func.get("mst-admin/notifications/analytics/crons");
      setCrons(res?.data?.data?.crons || res?.data?.crons || []);
    } catch {
      setError("Could not load cron data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered =
    category === "All"
      ? crons
      : crons.filter((c) => c.category?.toLowerCase() === category.toLowerCase());

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl mb-3" />
        <p className="text-sm mb-3">{error}</p>
        <button
          onClick={load}
          className="text-indigo-600 text-sm font-medium flex items-center gap-1 hover:underline"
        >
          <FontAwesomeIcon icon={faRotate} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Category filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <FontAwesomeIcon icon={faFilter} className="text-gray-400 text-sm" />
        {CRON_CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${
              category === cat
                ? "bg-indigo-600 text-white shadow-sm"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Cron grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-gray-400">
          <FontAwesomeIcon icon={faRobot} className="text-4xl mb-3" />
          <p className="text-sm">No crons in this category</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((cron) => (
            <div
              key={cron.name}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: cron.color || "#6366F1" }}
                  />
                  <p className="font-semibold text-gray-900 text-sm truncate">
                    {cron.label || cron.name}
                  </p>
                </div>
                <span
                  className="text-xs font-medium px-1.5 py-0.5 rounded capitalize flex-shrink-0 ml-2"
                  style={{
                    backgroundColor: (cron.color || "#6366F1") + "18",
                    color: cron.color || "#6366F1",
                  }}
                >
                  {cron.category || "–"}
                </span>
              </div>

              {/* Schedule */}
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                <FontAwesomeIcon icon={faClock} className="text-gray-300" />
                {cron.schedule || "–"}
              </div>

              {/* Last run info */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Last run</span>
                  <CronStatus lastRunAt={cron.lastRunAt} />
                </div>
                <p className="text-xs text-gray-700 font-medium">
                  {fmtDate(cron.lastRunAt)}
                </p>
                {cron.lastRunAt && (
                  <p className="text-xs text-gray-500">
                    <span className="font-semibold text-gray-800">
                      {fmtNum(cron.lastRunSent)}
                    </span>{" "}
                    sent /{" "}
                    <span className="font-semibold text-gray-800">
                      {fmtNum(cron.lastRunMatched)}
                    </span>{" "}
                    matched
                  </p>
                )}
              </div>

              {/* Sparkline */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400">7-day sends</span>
                  <span className="text-xs font-semibold text-gray-700">
                    {fmtNum(cron.totalSent7d)} total
                  </span>
                </div>
                <Sparkline data={cron.sparkline} color={cron.color || "#6366F1"} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Tab: Token Health ────────────────────────────────────────────────────────
const TokenHealthTab = ({ tokenHealth, loading, error, reload }) => {
  const th = tokenHealth || {};

  const kpis = [
    {
      icon: faMobileAlt,
      iconColor: "#6366F1",
      title: "Active FCM Tokens",
      value: fmtNum(th.activeTokens),
      sub: "Live push endpoints",
    },
    {
      icon: faShieldAlt,
      iconColor: "#059669",
      title: "Token Coverage",
      value: fmtPct(th.tokenCoverage),
      sub: `${fmtNum(th.activeTokens)} of ${fmtNum(th.totalSeekers)} seekers`,
    },
    {
      icon: faBan,
      iconColor: "#7C3AED",
      title: "Opted-out Behavioral",
      value: fmtNum(th.optedOutBehavioral),
      sub: th.totalSeekers
        ? fmtPct((th.optedOutBehavioral / th.totalSeekers) * 100) + " of users"
        : "–",
    },
    {
      icon: faExclamationTriangle,
      iconColor: "#EA580C",
      title: "Capped Today",
      value: fmtNum(th.capHitsToday),
      sub: "Hit daily 2-notif cap",
    },
  ];

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl mb-3" />
        <p className="text-sm mb-3">{error}</p>
        <button
          onClick={reload}
          className="text-indigo-600 text-sm font-medium flex items-center gap-1 hover:underline"
        >
          <FontAwesomeIcon icon={faRotate} /> Retry
        </button>
      </div>
    );
  }

  const behaviorPct = th.totalSeekers
    ? (th.optedOutBehavioral / th.totalSeekers) * 100
    : 0;
  const reEngPct = th.totalSeekers
    ? (th.optedOutReEngagement / th.totalSeekers) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <KpiCard key={k.title} {...k} loading={loading} />
        ))}
      </div>

      {/* Donut + Opt-out panel */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Donut */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col items-center justify-center">
          <h3 className="font-semibold text-gray-800 mb-4 self-start">
            Token Coverage
          </h3>
          {loading ? (
            <Skeleton className="w-40 h-40 rounded-full" />
          ) : (
            <DonutChart pct={Number(th.tokenCoverage || 0)} label="coverage" />
          )}
          <div className="mt-4 grid grid-cols-2 gap-4 w-full">
            <div className="text-center">
              <p className="text-2xl font-bold text-indigo-600">
                {loading ? "–" : fmtNum(th.activeTokens)}
              </p>
              <p className="text-xs text-gray-500">Active tokens</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-600">
                {loading ? "–" : fmtNum(th.totalSeekers)}
              </p>
              <p className="text-xs text-gray-500">Total seekers</p>
            </div>
          </div>
        </div>

        {/* Opt-out breakdown */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col gap-5">
          <h3 className="font-semibold text-gray-800">Opt-out Breakdown</h3>

          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-gray-600">
                  <FontAwesomeIcon icon={faRobot} className="text-violet-400 mr-1.5" />
                  Behavioral opted out
                </span>
                <span className="text-sm font-semibold text-gray-800">
                  {loading ? "–" : `${fmtNum(th.optedOutBehavioral)} users (${fmtPct(behaviorPct)})`}
                </span>
              </div>
              {loading ? (
                <Skeleton className="h-3 w-full" />
              ) : (
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className="h-3 rounded-full"
                    style={{
                      width: `${Math.min(behaviorPct, 100)}%`,
                      background: "linear-gradient(to right, #7C3AED, #6366F1)",
                    }}
                  />
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Opted out of behavioral nudge notifications
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-gray-600">
                  <FontAwesomeIcon icon={faRotate} className="text-orange-400 mr-1.5" />
                  Re-engagement opted out
                </span>
                <span className="text-sm font-semibold text-gray-800">
                  {loading
                    ? "–"
                    : `${fmtNum(th.optedOutReEngagement)} users (${fmtPct(reEngPct)})`}
                </span>
              </div>
              {loading ? (
                <Skeleton className="h-3 w-full" />
              ) : (
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className="h-3 rounded-full"
                    style={{
                      width: `${Math.min(reEngPct, 100)}%`,
                      background: "linear-gradient(to right, #EA580C, #F59E0B)",
                    }}
                  />
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Opted out of re-engagement campaigns
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-indigo-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Daily Cap Info</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Each user receives a maximum of <strong>2 behavioral notifications per day</strong>.
                Today,{" "}
                <strong className="text-indigo-700">{loading ? "..." : fmtNum(th.capHitsToday)}</strong>{" "}
                users hit their daily cap and subsequent notifications were suppressed.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faMobileAlt} className="text-gray-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Stale Token Note</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Stale FCM tokens are automatically pruned when broadcasts are sent. Tokens
                appear here when users uninstall or clear app data. The platform maintains
                token freshness through FCM delivery feedback.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Root component ───────────────────────────────────────────────────────────
export default function NotificationsHub() {
  const [tab, setTab] = useState("overview");
  const [period, setPeriod] = useState(30);
  const [tokenHealth, setTokenHealth] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [tokenError, setTokenError] = useState(null);

  const loadTokenHealth = useCallback(async () => {
    setTokenLoading(true);
    setTokenError(null);
    try {
      const res = await func.get("mst-admin/notifications/analytics/token-health");
      setTokenHealth(res?.data?.data || res?.data || null);
    } catch {
      setTokenError("Could not load token health.");
    } finally {
      setTokenLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokenHealth();
  }, [loadTokenHealth]);

  const TABS = [
    { key: "overview", label: "Overview", icon: faChartLine },
    { key: "broadcasts", label: "Broadcasts", icon: faBullhorn },
    { key: "crons", label: "Behavioral Crons", icon: faRobot },
    { key: "tokens", label: "Token Health", icon: faShieldAlt },
  ];

  const PERIODS = [7, 30, 90];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* ── Gradient header ── */}
      <div className="bg-[linear-gradient(to_right,_#19224B,_#4C1D95)] rounded-xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <FontAwesomeIcon icon={faBell} className="text-white text-lg" />
              </div>
              <h1 className="text-2xl font-bold text-white tracking-tight">
                Notification Intelligence
              </h1>
            </div>
            <p className="text-indigo-200 text-sm ml-13">
              Real-time analytics for broadcasts, behavioral crons &amp; trigger engine
            </p>
          </div>

          {/* Period selector */}
          <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${
                  period === p
                    ? "bg-white text-indigo-700 shadow"
                    : "text-indigo-200 hover:text-white"
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>

        {/* Tab bar inside header */}
        <div className="flex items-center gap-1 mt-5 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                tab === t.key
                  ? "bg-white text-indigo-700 shadow"
                  : "text-indigo-200 hover:bg-white/10"
              }`}
            >
              <FontAwesomeIcon icon={t.icon} className="text-xs" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div>
        {tab === "overview" && (
          <OverviewTab period={period} tokenHealth={tokenHealth} />
        )}
        {tab === "broadcasts" && <BroadcastsTab />}
        {tab === "crons" && <CronsTab />}
        {tab === "tokens" && (
          <TokenHealthTab
            tokenHealth={tokenHealth}
            loading={tokenLoading}
            error={tokenError}
            reload={loadTokenHealth}
          />
        )}
      </div>
    </div>
  );
}
